// A station's own screen (option A, Oct 2026): one card per table, one tap per dish. The
// same rules as backend/kitchen.py `line_at` and `tick_line`: a dish is at the first station
// on its route that hasn't ticked it off, and at the pass once they all have.
import { PASS } from '@verlo/shared/lib/kitchenRoutes';

/** The stations a line goes through before the pass. */
export const routeOf = (line) => {
  if (line?.route?.length) return line.route;
  return line?.station && line.station !== PASS ? [line.station] : [];
};

/** Where a dish is now: a station's key, or the pass. */
export const lineAt = (line) => {
  const done = line?.stations_done || {};
  return routeOf(line).find((st) => !done[st]) || PASS;
};

/** Is a dish through to the table? Past every station on its route; a dish the Pass makes
 *  itself (no route) once the Pass has ticked it off (backend `kitchen.line_through`, M1). */
export const lineThrough = (line) => (routeOf(line).length
  ? lineAt(line) === PASS
  : Boolean((line?.stations_done || {})[PASS]));

/** One dish on a ticket. The parts of a meal share the meal's `line_id` (backend
 *  `meals.explode`), so a dish is its line and its `menu_item_id`, as `kitchen.tick_line`
 *  reads it: without it the burger and the fries of one combo were one row. */
export const lineKey = (line) => `${line?.line_id}:${line?.menu_item_id || ''}`;

/** A ticket's items with one dish ticked off (or the tick taken back) at `station`: what the
 *  screen shows until the server answers. Only that dish, not the rest of its meal. */
export function withTick(items, line, station, done, stamp) {
  const key = lineKey(line);
  return (items || []).map((l) => {
    if (l.voided || lineKey(l) !== key) return l;
    const sd = { ...(l.stations_done || {}) };
    if (done) sd[station] = stamp; else delete sd[station];
    return { ...l, stations_done: sd };
  });
}

/** Does a card need the allergy border: a dish on it the table can't have, or one whose
 *  allergens the till couldn't read ("Check: …"), as on the kitchen's own ticket card. */
export const needsAllergyCheck = (lines) => (lines || []).some(
  (l) => !l.voided && (l.allergen_clash?.length > 0 || l.allergen_unread?.length > 0),
);

const nameOf = (key, stations) => (key === PASS ? 'the Pass' : (stations || []).find((s) => s.key === key)?.name || key);

/** "from Chicken, then Build": where a dish at `station` has come from and goes next. */
export function whereWords(line, station, stations) {
  const route = routeOf(line);
  const i = route.indexOf(station);
  const prev = i > 0 ? route[i - 1] : null;
  const next = i >= 0 && i < route.length - 1 ? route[i + 1] : PASS;
  return [prev && `from ${nameOf(prev, stations)}`, `then ${nameOf(next, stations)}`].filter(Boolean).join(', ');
}

export const atWords = (line, stations) => {
  if (!routeOf(line).length) return lineThrough(line) ? 'ready' : 'make at the Pass';
  const at = lineAt(line);
  return at === PASS ? 'ready' : `at ${nameOf(at, stations)}`;
};

/** How long a just-ticked dish stays on its card with Undo (M4, Oct 2026). */
export const UNDO_MS = 5000;

/** The minutes a ticket has waited, as the board's colour: copper after 10, claret after 20. */
export const lateness = (minutes) => (minutes >= 20 ? 'very-late' : minutes >= 10 ? 'late' : null);

/**
 * The board for `station`: each ticket that has a dish for it, sorted into
 * `todo` (a dish is here now), `coming` (its dishes are still upstream) and `done` (all ticked
 * off here, in the last `doneForMs`). Each comes with its rows: `here`, `upstream`, `doneHere`,
 * and `elsewhere`, the ticket's other dishes, for "+ 1 on Build". `recent` is the dishes (by
 * `lineKey`) ticked here in the last few seconds: they stay on their card as `justDone`, with
 * Undo, and keep the card where it was until they go.
 */
export function stationBoard(tickets, station, { now = Date.now(), doneForMs = 10 * 60000, recent = new Set() } = {}) {
  const todo = [];
  const coming = [];
  const done = [];
  for (const t of tickets || []) {
    const live = (t.items || []).filter((l) => !l.voided);
    const mine = live.filter((l) => routeOf(l).includes(station));
    if (!mine.length) continue;
    const here = mine.filter((l) => lineAt(l) === station);
    const upstream = mine.filter((l) => {
      const at = lineAt(l);
      return at !== PASS && routeOf(l).indexOf(at) < routeOf(l).indexOf(station);
    });
    const doneHere = mine.filter((l) => (l.stations_done || {})[station]);
    const elsewhere = live.filter((l) => !routeOf(l).includes(station));
    const justDone = doneHere.filter((l) => recent.has(lineKey(l)));
    const card = { ticket: t, here, upstream, doneHere, justDone, elsewhere };
    if (here.length || justDone.length) todo.push(card);
    else if (upstream.length) coming.push(card);
    else {
      const last = Math.max(...doneHere.map((l) => new Date(l.stations_done[station]).getTime()));
      if (now - last <= doneForMs) done.push({ ...card, doneAt: last });
    }
  }
  done.sort((a, b) => b.doneAt - a.doneAt);
  return { todo, coming, done };
}
